import { ProcessedFileStatus } from 'api/menu-service/menu-service.types';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  BackgroundUpload,
  BackgroundUploadContext,
  BackgroundUploadContextType,
  FileUploadObject,
} from './background-upload.context';
import { useQueue } from 'hooks/queue.hooks';

type UseInitBackgroundUploadsOptions = {
  maxConcurrent: number;
};

type FileUploadQueueObject = {
  uploadId: string;
  key: string;
  uploadFn: BackgroundUpload['uploadFn'];
  last: boolean;
} & FileUploadObject;

const updateUpload = (
  setUploads,
  id: BackgroundUpload['id'],
  newUpload: Partial<BackgroundUpload>,
) => {
  setUploads(curr => ({
    ...curr,
    [id]: {
      ...curr[id],
      ...newUpload,
    },
  }));
};

const updateFileUpload = (
  setUploads,
  fileUpload,
  status: ProcessedFileStatus,
  statusMessage = '',
) => {
  setUploads(curr => ({
    ...curr,
    [fileUpload.uploadId]: {
      ...curr[fileUpload.uploadId],
      files: {
        ...curr[fileUpload.uploadId].files,
        [fileUpload.key]: {
          ...curr[fileUpload.uploadId].files[fileUpload.key],
          status,
          statusMessage,
        },
      },
    },
  }));
};

export const useInitBackgroundUploads = (
  optionsArg: UseInitBackgroundUploadsOptions,
): BackgroundUploadContextType => {
  const options = useRef(optionsArg);

  const [uploads, setUploads] = useState<Record<string, BackgroundUpload>>({});
  const [uploading, setUploading] = useState<
    Record<string, FileUploadQueueObject>
  >({});
  const { add, remove, size } = useQueue<FileUploadQueueObject>();

  const addUpload = (upload: Omit<BackgroundUpload, 'uploadComplete'>) => {
    setUploads(curr => ({
      ...curr,
      [upload.id]: { ...upload, uploadComplete: false },
    }));
    Object.entries(upload.files).forEach(([key, fileUpload], i) => {
      add({
        ...fileUpload,
        uploadId: upload.id,
        key,
        uploadFn: upload.uploadFn,
        last: i === Object.keys(upload.files).length - 1,
      });
    });
  };

  useEffect(() => {
    const execute = async () => {
      if (
        size > 0 &&
        Object.keys(uploading).length < options.current.maxConcurrent
      ) {
        const fileUpload = remove();
        setUploading(current => ({
          ...current,
          [`${fileUpload.uploadId}-${fileUpload.key}`]: fileUpload,
        }));

        try {
          updateFileUpload(setUploads, fileUpload, 'UPLOADING');
          await fileUpload.uploadFn(fileUpload);
          updateFileUpload(setUploads, fileUpload, 'UPLOADED');
        } catch (e) {
          updateFileUpload(setUploads, fileUpload, 'ERROR', e.message);
        } finally {
          setUploading(current => {
            const next = { ...current };
            delete next[`${fileUpload.uploadId}-${fileUpload.key}`];
            return next;
          });
        }
      }
    };

    execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, remove, uploading]);

  useEffect(() => {
    Object.values(uploads).forEach(upload => {
      if (
        !upload.uploadComplete &&
        Object.values(upload.files).every(
          file => file.status === 'UPLOADED' || file.status === 'ERROR',
        )
      ) {
        updateUpload(setUploads, upload.id, { uploadComplete: true });
        upload.onComplete(upload);
      }
    });
  }, [uploads]);

  return { uploads, addUpload };
};

export const useBackgroundUploads = (): BackgroundUploadContextType =>
  useContext(BackgroundUploadContext);

export const useBackgroundUpload = (uploadId: string) => {
  const backgroundUpload = useBackgroundUploads().uploads[uploadId];

  const successes = backgroundUpload
    ? Object.values(backgroundUpload?.files)?.filter(
        fileUpload => fileUpload.status === 'UPLOADED',
      )
    : [];
  const errors = backgroundUpload
    ? Object.values(backgroundUpload?.files)?.filter(
        fileUpload => fileUpload.status === 'ERROR',
      )
    : [];

  return { backgroundUpload, successes, errors };
};
